import { type CSSProperties, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TurnoverBin } from "../../api/finance";
import { money } from "./money";
import { formatMoney } from "../../lib/format";

/**
 * Compact monthly-turnover column chart. Single series (invoiced turnover per
 * period), so it carries one brand hue with no legend — the section title names
 * it. Bar form for discrete periods; the fill follows --fin-chart-fill, which
 * each theme sets for contrast on its own surface. Value-axis ticks in clean
 * rounded numbers, per-bar hover tooltip with the currency formatted, and a
 * visually-hidden data table so the values are never color- or hover-only.
 *
 * Turnover values are `invoicedEur` — always EUR regardless of account currency.
 */
export function TurnoverChart({ periods, currency = "EUR" }: { periods: TurnoverBin[]; currency?: string }) {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(640);
  const [hover, setHover] = useState<number | null>(null);
  const [tip, setTip] = useState<{ text: string; x: number; y: number } | null>(null);

  // Measure the container so the SVG renders at real pixel width — crisp text,
  // no preserveAspectRatio distortion, and precise control over bar caps/gaps.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const cw = entries[0]?.contentRect.width;
      if (cw && cw > 0) setW(cw);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (periods.length === 0) return null;

  const h = 200;
  const pad = { top: 10, right: 12, bottom: 26, left: 60 };
  const innerW = Math.max(0, w - pad.left - pad.right);
  const innerH = h - pad.top - pad.bottom;

  const values = periods.map((p) => p.invoicedEur);
  const rawMax = Math.max(...values, 0);
  // A month can fall below zero: a cancelled invoice takes from the month of the cancellation,
  // and in a month with more cancelled than invoiced the column goes down from the zero line.
  const rawMin = Math.min(...values, 0);
  const min = rawMin < 0 ? -niceMax(-rawMin) : 0;
  // With no month above zero the axis ends at zero: a top of one would crowd three ticks into the
  // zero line.
  const max = rawMax > 0 || min === 0 ? niceMax(rawMax) : 0;
  const yOf = (v: number) => pad.top + ((max - v) / (max - min)) * innerH;
  const baselineY = yOf(0);
  const n = periods.length;
  const bandW = innerW / n;
  const barW = Math.max(4, Math.min(24, bandW * 0.62));
  const labelEvery = Math.max(1, Math.ceil(34 / bandW));

  const ticks = 4;
  const tickValues = min < 0
    ? (max > 0 ? [min, 0, max / 2, max] : [min, min / 2, 0])
    : Array.from({ length: ticks + 1 }, (_, i) => (max * i) / ticks);
  const compact = (v: number) =>
    formatMoney(v, currency, undefined, { notation: "compact", maximumFractionDigits: 1 });

  const showTip = (i: number) => (e: React.MouseEvent) =>
    setTip({ text: `${periods[i].label}: ${money(values[i], currency)}`, x: e.clientX + 12, y: e.clientY - 30 });

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div style={subtitle}>{t("finance.turnover.subtitle", "Invoices per period, incl. VAT, without the opening balance and other postings")}</div>
      {tip && (
        <div className="sd-tooltip" style={{ "--tip-x": `${tip.x}px`, "--tip-y": `${tip.y}px` } as CSSProperties}>
          {tip.text}
        </div>
      )}
      <svg
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label={t("finance.turnover.aria", "Bar chart of invoiced turnover per period")}
        style={{ display: "block", maxWidth: "100%" }}
      >
        {/* Value axis: hairline gridlines + clean rounded tick labels */}
        {tickValues.map((value, i) => {
          const y = yOf(value);
          return (
            <g key={`t-${i}`}>
              <line x1={pad.left} x2={w - pad.right} y1={y} y2={y} stroke="var(--border)" strokeWidth={1} />
              <text x={pad.left - 8} y={y + 3.5} textAnchor="end" fontSize={10} fill="var(--ink-3)" style={tabular}>
                {compact(value)}
              </text>
            </g>
          );
        })}

        {/* Columns — 4px rounded at the data end, square at the zero line */}
        {periods.map((p, i) => {
          const barH = Math.abs(yOf(p.invoicedEur) - baselineY);
          const x = pad.left + i * bandW + (bandW - barW) / 2;
          const active = hover === null || hover === i;
          return (
            <g key={`${p.label}-${i}`}>
              {barH > 0 && (
                <path
                  d={p.invoicedEur > 0
                    ? barPath(x, baselineY - barH, barW, barH, 4)
                    : barPathDown(x, baselineY, barW, barH, 4)}
                  fill="var(--fin-chart-fill)"
                  opacity={active ? 1 : 0.4}
                  style={{ transition: "opacity .12s" }}
                  onMouseEnter={(e) => { setHover(i); showTip(i)(e); }}
                  onMouseMove={showTip(i)}
                  onMouseLeave={() => { setHover(null); setTip(null); }}
                >
                  <title>{`${p.label}: ${money(p.invoicedEur, currency)}`}</title>
                </path>
              )}
              {i % labelEvery === 0 && (
                <text x={pad.left + i * bandW + bandW / 2} y={h - 8} textAnchor="middle" fontSize={10} fill="var(--ink-3)">
                  {p.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Table view — values never gated behind hover or color */}
      <table style={visuallyHidden}>
        <caption>{t("finance.turnover.title", "Turnover")}</caption>
        <thead>
          <tr>
            <th>{t("finance.period.label", "Period")}</th>
            <th>{t("finance.payments.amount", "Amount")}</th>
          </tr>
        </thead>
        <tbody>
          {periods.map((p, i) => (
            <tr key={`row-${p.label}-${i}`}>
              <td>{p.label}</td>
              <td>{money(p.invoicedEur, currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Round a value up to a clean 1 / 2 / 2.5 / 5 × 10^k so axis ticks stay tidy. */
function niceMax(v: number): number {
  if (v <= 0) return 1;
  const exp = Math.floor(Math.log10(v));
  const base = Math.pow(10, exp);
  const frac = v / base;
  const nice = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 2.5 ? 2.5 : frac <= 5 ? 5 : 10;
  return nice * base;
}

/** A column below the zero line: square at the line, rounded at the bottom (the data end). */
function barPathDown(x: number, y: number, width: number, height: number, r: number): string {
  const rr = Math.max(0, Math.min(r, width / 2, height));
  return (
    `M${x},${y}` +
    `L${x},${y + height - rr}` +
    `Q${x},${y + height} ${x + rr},${y + height}` +
    `L${x + width - rr},${y + height}` +
    `Q${x + width},${y + height} ${x + width},${y + height - rr}` +
    `L${x + width},${y}Z`
  );
}

/** Column path with a rounded top (the data end) and square feet on the baseline. */
function barPath(x: number, y: number, width: number, height: number, r: number): string {
  const rr = Math.max(0, Math.min(r, width / 2, height));
  return (
    `M${x},${y + height}` +
    `L${x},${y + rr}` +
    `Q${x},${y} ${x + rr},${y}` +
    `L${x + width - rr},${y}` +
    `Q${x + width},${y} ${x + width},${y + rr}` +
    `L${x + width},${y + height}Z`
  );
}

const subtitle: CSSProperties = { fontSize: 12, color: "var(--ink-3)", marginBottom: 10 };
const tabular: CSSProperties = { fontVariantNumeric: "tabular-nums" };
const visuallyHidden: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};
